import { fetchCategoryGamesHandler } from '~/handlers/fetch-category-games.handler';

/**
 * Monta o `HomeSectionData` da página a partir das categorias do perfil oficial.
 *
 * O `GamesPage.vue` já sabe renderizar `HomeSectionItem[]` (é o que
 * `/api/games/sections/{slug}` devolve), então em vez de reescrever a página a
 * gente sintetiza o mesmo shape a partir de `profile.categories` +
 * `/api/games/categories/{slug}`. O payload público de categoria do core é
 * exatamente um `HomeCategoryData`, então o encaixe é direto.
 */

/** Categoria sintética pros PLACEHOLDER, que não têm jogos pra buscar. */
function placeholderCategoryData(
  category: OfficialProfileCategory,
): HomeCategoryData {
  return {
    // O GamesPage só lê `data.slug` num PLACEHOLDER (pra descobrir qual bloco
    // especial renderizar); o resto existe só pra satisfazer o shape.
    id: 0,
    name: category.name,
    slug: category.slug,
    created_at: '',
    updated_at: '',
    show_in_filter: false,
    games: [],
  };
}

/**
 * Uma categoria TABS do perfil vira um swiper de uma aba só.
 *
 * No modelo antigo, TABS era uma *Section* com categorias filhas. No modelo de
 * perfil oficial, TABS é uma categoria única — não existe filho pra virar aba.
 * Renderizar como aba única mantém o layout de abas sem inventar dado; se as
 * abas de verdade forem necessárias, o core precisa expor os filhos.
 */
function tabsDataFromCategory(
  category: OfficialProfileCategory,
  data: HomeCategoryData,
): HomeTabsData {
  return {
    id: data.id,
    name: category.name,
    slug: category.slug,
    priority: category.priority,
    is_active: true,
    created_at: data.created_at,
    updated_at: data.updated_at,
    categories: [
      {
        ...data,
        item_config: {
          content_type: category.content_type,
          limit: null,
          icon: null,
          see_more_url: null,
        },
      },
    ],
  };
}

async function buildSectionItem(
  category: OfficialProfileCategory,
): Promise<HomeSectionItem | null> {
  if (category.content_type === 'PLACEHOLDER') {
    return {
      type: 'category',
      priority: category.priority,
      content_type: 'PLACEHOLDER',
      limit: null,
      icon: null,
      see_more_url: null,
      data: placeholderCategoryData(category),
    };
  }

  const { category: data } = await fetchCategoryGamesHandler({
    slug: category.slug,
  });

  // Categoria sem jogos (ou inexistente) responde 404 no core — a seção
  // simplesmente não entra na página, em vez de renderizar um carrossel vazio.
  if (!data) return null;

  if (category.content_type === 'TABS') {
    return {
      type: 'section',
      priority: category.priority,
      content_type: 'TABS',
      limit: null,
      icon: null,
      see_more_url: null,
      data: tabsDataFromCategory(category, data),
    };
  }

  // BASIC e NUMBER → lista de jogos. NUMBER liga a numeração no GamesPage.
  return {
    type: 'category',
    priority: category.priority,
    content_type: category.content_type,
    limit: null,
    icon: null,
    see_more_url: null,
    data,
  };
}

export async function buildSectionDataFromOfficialProfile(
  profile: OfficialProfileDetail,
): Promise<HomeSectionData> {
  const items = await Promise.all(
    profile.categories.map((category) => buildSectionItem(category)),
  );

  return {
    id: 0,
    name: profile.name,
    slug: profile.slug,
    priority: 0,
    is_active: true,
    items: items.filter((item): item is HomeSectionItem => item !== null),
  };
}
